import { makeStyles } from '@rbx/ui';

const useDetailsSwaggerUIStyles = makeStyles()((theme) => ({
  // Main container for both layout and operation components
  container: {
    /**
     * Container overrides
     */

    // Swagger UI override defaults
    '& .swagger-ui': {
      color: theme.palette.content.muted,
      ...theme.typography.body1,

      // Operation override defaults
      '& .opblock': {
        border: 'none',
        margin: 0,
        boxShadow: 'none',
        backgroundColor: 'transparent',
      },

      // Section header container override defaults
      // Section headers correspond to the Request and Response sections
      '& .opblock .opblock-section-header': {
        backgroundColor: 'transparent',
        minHeight: 'auto',
        boxShadow: 'none',
        padding: '6px 0',
      },

      // Section header title override defaults
      '& .opblock .opblock-section-header h4': {
        color: theme.palette.content.standard,
        ...theme.typography.h6,
      },

      // Description font override
      '& .opblock-description-wrapper p, & .opblock-external-docs-wrapper p, & .opblock-title_normal p':
        {
          color: theme.palette.content.muted,
          ...theme.typography.body1,
        },

      // Table container
      '& .table-container': {
        // Reducing padding for density (default 20)
        padding: '10px',
      },

      // Table headers
      '& table thead tr td, & table thead tr th': {
        color: theme.palette.content.standard,
        // Removing vertical padding for density (default 12)
        padding: '4px 0',
        ...theme.typography.smallLabel2,
        // Ensure all headers are 12, including the response code and links headers (default 14)
        fontSize: '12px',
      },

      // Tab options
      '& .tab li button.tablinks': {
        color: theme.palette.content.standard,
        ...theme.typography.smallLabel1,
        // Ensure that the selected tab can still be bolded by changing the font weight
        fontWeight: undefined,
      },

      // Code blocks
      '& .opblock-body pre.microlight': {
        // Need !important because background is hardcoded into element
        background: `${theme.palette.mode === 'dark' ? theme.palette.surface[200] : theme.palette.content.static.dark} !important`,
        color: theme.palette.content.static.light,
        ...theme.typography.code,
        fontSize: '13px',
      },

      // Inline code
      '& .markdown code, & .renderedMarkdown code': {
        background: theme.palette.components.inlineCode.asText.fill,
        color: theme.palette.components.inlineCode.asText.color,
        ...theme.typography.code,
        // Matches existing markdown code
        fontSize: undefined,
        paddingInline: '4px',
        padding: '0px 4px',
      },

      /**
       * Parameters section overrides
       */

      // The parameter section header is a clickable tab, so we disable this
      '& .opblock .tab-header .tab-item': {
        pointerEvents: 'none',
      },

      // The parameter section header has a grey underline, which this overrides
      '& .opblock .tab-header .tab-item.active h4 span:after': {
        display: 'none',
      },

      // Hide Parameter header try it out since we roll our own
      '& .opblock .try-out': {
        display: 'none',
      },

      // No parameters message
      '& .opblock-description-wrapper': {
        // Reducing padding for density (default 15 20)
        padding: '0 12px',
      },

      // Parameter name
      '& .parameter__name': {
        color: theme.palette.content.standard,
        ...theme.typography.body1,
      },

      // Parameter name required
      '& .parameter__name.required span, & .parameter__name.required:after': {
        color: theme.palette.content.alert.important,
      },

      // Parameter type
      '& .parameter__type': {
        color: theme.palette.content.standard,
        ...theme.typography.smallLabel2,
      },

      // Parameter location
      '& .parameter__extension, & .parameter__in': {
        color: theme.palette.content.muted,
        ...theme.typography.code,
        fontSize: '13px',
        fontWeight: 400,
        fontStyle: 'italic',
      },

      // Parameter select
      '& input[type=email], & input[type=file], & input[type=password], & input[type=search], & input[type=text], & select, & textarea':
        {
          color:
            theme.palette.mode === 'dark'
              ? theme.palette.content.static.light
              : theme.palette.content.static.dark,
          background: theme.palette.surface[200],
          borderColor: theme.palette.content.disabled,
          appearance: 'auto',
        },

      '& input[disabled], & select[disabled], & textarea[disabled]': {
        color: theme.palette.content.muted,
        background: theme.palette.surface[200],
        borderColor: theme.palette.content.disabled,
        appearance: 'auto',
      },

      '& input[type=email].invalid, & input[type=file].invalid, & input[type=password].invalid, & input[type=search].invalid, & input[type=text].invalid, & textarea.invalid':
        {
          borderColor: theme.palette.content.alert.important,
          backgroundColor: theme.palette.surface[200],
        },

      // Parameter array add/remove buttons
      '& .btn': {
        color: theme.palette.content.standard,
        backgroundColor: theme.palette.surface[200],
        borderColor: theme.palette.content.disabled,
        ...theme.typography.buttonSmall,
        '&:hover': {
          color: theme.palette.content.standard,
          backgroundColor: theme.palette.surface[300],
          borderColor: theme.palette.content.disabled,
        },
      },

      // Parameter array remove button
      '& .json-schema-form-item-remove': {
        // Add a bit of margin, currently no spacing between the input box and the remove button
        marginLeft: '4px',
        // Make the button square
        padding: '4px 8px',
      },

      /**
       * Validation errors overrides
       */

      // Validation error box
      '& .errors-wrapper': {
        borderColor: theme.palette.content.alert.important,
      },

      /**
       * Responses section overrides
       */

      // Response controls
      '& .response-controls': {
        // Hide the entire control section since we don't care about response media type
        display: 'none',
      },

      // Response links
      '& .response-col_links': {
        // Hide the entire column since we don't have links in our docs
        display: 'none',
      },

      // Response code
      '& .response-col_status': {
        color: theme.palette.content.standard,
        ...theme.typography.smallLabel2,
        fontWeight: undefined,
        fontSize: undefined,
      },

      // Response download
      '& .download-contents': {
        // Hide since this isn't too useful when there already is copy to clipboard
        display: 'none',
      },

      // Response copy to clipboard
      '& .copy-to-clipboard': {
        // Accounting for the disappearance of the download contents (default 100)
        right: '10px',
      },

      // Execution response inner
      '& .responses-inner': {
        // Reducing padding for density (default 20)
        padding: '10px',
      },

      // Execution response undocumented
      '& .response-col_status .response-undocumented': {
        color: theme.palette.content.muted,
        ...theme.typography.code,
        fontSize: undefined,
      },

      // Execution response headers
      '& .responses-inner h4': {
        color: theme.palette.content.standard,
        ...theme.typography.smallLabel2,
        fontSize: '16px',
      },

      '& .responses-inner h5': {
        color: theme.palette.content.standard,
        ...theme.typography.smallLabel2,
        fontSize: '14px',
      },

      /**
       * Model overrides
       */

      // Model Container
      '& .model-box': {
        minWidth: '100%',
        width: 'fit-content',
        backgroundColor: theme.palette.surface[300],
      },

      // Model

      // Affects model property name font
      '& .model': {
        color: theme.palette.content.standard,
        ...theme.typography.body2,
        fontSize: undefined,
      },

      // Affects model property description font
      '& .model p': {
        color: theme.palette.content.muted,
        ...theme.typography.body2,
        fontSize: '12px',
        marginBottom: 0,
      },

      '& .model p span': {
        fontSize: '12px',
      },

      // Model toggle
      '& .model-toggle:after': {
        background: `${
          theme.palette.mode === 'dark'
            ? `url("data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path fill='white' d='M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'/></svg>") 50% no-repeat`
            : `url("data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path fill='black' d='M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'/></svg>") 50% no-repeat`
        }`,
      },

      // Model title
      '& .model-title': {
        color: theme.palette.content.standard,
        ...theme.typography.body2,
        fontSize: undefined,
        fontWeight: undefined,
      },

      // Model description
      '& .model .external-docs, & table.model tr.description': {
        color: theme.palette.content.standard,
        ...theme.typography.smallLabel1,
        fontSize: undefined,
        fontWeight: undefined,
      },

      // Model required
      '& table.model tr.property-row .star': {
        color: theme.palette.content.alert.important,
      },

      // Model hint
      '& .model-title:hover .model-hint': {
        // Prevent the hover hint from appearing
        display: 'none',
      },

      // Model property type
      '& .model .prop-type': {
        color: theme.palette.content.action,
      },

      // Model property primitive
      '& .model .property.primitive': {
        color: theme.palette.content.muted,
      },

      // Model property format
      '& .prop-format': {
        color: theme.palette.content.muted,
      },

      /**
       * Execution overrides
       */

      // Button group
      '& .btn-group': {
        // Reducing padding for density (default 30)
        padding: '10px',
      },

      // Execute button
      '& .btn.execute': {
        ...theme.typography.buttonSmall,
        color: theme.palette.content.static.light,
        backgroundColor: theme.palette.actionV2.primaryBrand.fill,
        borderColor: theme.palette.actionV2.primaryBrand.fill,
        '&:hover': {
          backgroundColor: theme.palette.actionV2.primaryBrand.containedHoverFocus,
          borderColor: theme.palette.actionV2.primaryBrand.containedHoverFocus,
        },
      },

      // Clear button
      '& .btn-clear': {
        ...theme.typography.buttonSmall,
        color: `${theme.palette.mode === 'dark' ? theme.palette.content.static.light : theme.palette.content.static.dark}`,
        backgroundColor: theme.palette.actionV2.secondary.fill,
        borderColor: theme.palette.actionV2.secondary.fill,
        '&:hover': {
          backgroundColor: theme.palette.actionV2.secondary.containedHoverFocus,
          borderColor: theme.palette.actionV2.secondary.containedHoverFocus,
        },
      },
    },
  },
}));

export default useDetailsSwaggerUIStyles;
